import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, Minus, RefreshCw, AlertCircle, ChevronRight, Calculator, Sparkles } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { getTrendPrediction } from '../../services/api';

interface TrendChartProps {
    projectId: string;
}

const METRIC_OPTIONS = [
    { value: 'page_views', label: 'Page Views' },
    { value: 'leads_captured', label: 'Leads Captured' },
    { value: 'email_opens', label: 'Email Opens' },
    { value: 'form_submissions', label: 'Form Submissions' },
    { value: 'unique_visitors', label: 'Unique Visitors' },
];

const HORIZON_OPTIONS = [
    { value: 7, label: '7 days' },
    { value: 14, label: '14 days' },
    { value: 30, label: '30 days' },
];

type ForecastMethod = 'holt_linear' | 'llm_heuristic';

interface ForecastIntervalPoint {
    date: string;
    value: number;
    lower: number;
    upper: number;
}

interface TrendData {
    metric_type: string;
    horizon_days: number;
    method?: ForecastMethod;
    prediction: {
        trend_direction: 'up' | 'down' | 'stable';
        predicted_value: number;
        confidence: number;
        reasoning: string;
        recommended_actions: string[];
        historical_context?: string;
        method?: ForecastMethod;
        forecast_interval?: ForecastIntervalPoint[];
        stats?: {
            n_days?: number;
            slope_per_day?: number;
            residual_std?: number;
            [key: string]: any;
        };
    };
    generated_at: string;
}

const METHOD_BADGES: Record<ForecastMethod, { label: string; variant: 'success' | 'warning' }> = {
    holt_linear: { label: 'Computed forecast', variant: 'success' },
    llm_heuristic: { label: 'AI estimate', variant: 'warning' },
};

function getMethodBadge(method: ForecastMethod | undefined) {
    if (!method || !METHOD_BADGES[method]) return null;
    const config = METHOD_BADGES[method];
    const icon = method === 'holt_linear'
        ? <Calculator size={12} style={{ marginRight: 4, verticalAlign: -2 }} />
        : <Sparkles size={12} style={{ marginRight: 4, verticalAlign: -2 }} />;
    return (
        <GlassBadge variant={config.variant} size="sm">
            {icon}{config.label}
        </GlassBadge>
    );
}

const TrendChart: React.FC<TrendChartProps> = ({ projectId }) => {
    const [metricType, setMetricType] = useState('page_views');
    const [horizonDays, setHorizonDays] = useState(14);
    const [data, setData] = useState<TrendData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchTrend = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getTrendPrediction(projectId, metricType, horizonDays);
            setData(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to generate trend prediction');
        } finally {
            setLoading(false);
        }
    }, [projectId, metricType, horizonDays]);

    useEffect(() => {
        fetchTrend();
    }, [fetchTrend]);

    const getTrendIcon = (direction: string) => {
        switch (direction) {
            case 'up': return <TrendingUp size={20} style={{ color: 'var(--success)' }} />;
            case 'down': return <TrendingDown size={20} style={{ color: 'var(--danger)' }} />;
            default: return <Minus size={20} style={{ color: 'var(--text-secondary)' }} />;
        }
    };

    const getTrendVariant = (direction: string): 'success' | 'danger' | 'default' => {
        switch (direction) {
            case 'up': return 'success';
            case 'down': return 'danger';
            default: return 'default';
        }
    };

    const getConfidenceVariant = (confidence: number): 'success' | 'warning' | 'danger' => {
        if (confidence >= 0.7) return 'success';
        if (confidence >= 0.4) return 'warning';
        return 'danger';
    };

    // Additive backend fields — undefined on legacy payloads
    const method: ForecastMethod | undefined = data?.method || data?.prediction?.method;
    const forecastInterval = data?.prediction?.forecast_interval;
    let intervalSummary: string | null = null;
    if (forecastInterval && forecastInterval.length > 0) {
        const first = forecastInterval[0];
        const last = forecastInterval[forecastInterval.length - 1];
        const firstHalfWidth = Math.max(0, (first.upper - first.lower) / 2);
        const lastHalfWidth = Math.max(0, (last.upper - last.lower) / 2);
        const fmt = (n: number) => Math.round(n).toLocaleString();
        intervalSummary = forecastInterval.length > 1
            ? `Forecast interval: ±${fmt(firstHalfWidth)} on day 1, widening to ±${fmt(lastHalfWidth)} by day ${forecastInterval.length}`
            : `Forecast interval: ±${fmt(lastHalfWidth)}`;
    }

    return (
        <div className="trend-chart">
            {/* Controls */}
            <GlassCard padding="md" className="trend-controls">
                <div className="trend-controls-row">
                    <div className="trend-select-group">
                        <label className="trend-label">Metric</label>
                        <select
                            className="trend-select"
                            value={metricType}
                            onChange={(e) => setMetricType(e.target.value)}
                        >
                            {METRIC_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="trend-select-group">
                        <label className="trend-label">Horizon</label>
                        <select
                            className="trend-select"
                            value={horizonDays}
                            onChange={(e) => setHorizonDays(Number(e.target.value))}
                        >
                            {HORIZON_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<RefreshCw size={14} />}
                        onClick={fetchTrend}
                        loading={loading}
                    >
                        Refresh
                    </GlassButton>
                </div>
            </GlassCard>

            {/* Loading State */}
            {loading && (
                <GlassCard padding="lg">
                    <div className="trend-loading">
                        <RefreshCw size={24} className="trend-spinner" style={{ color: 'var(--primary-500)' }} />
                        <p style={{ color: 'var(--text-secondary)', margin: '12px 0 0' }}>
                            Generating prediction with AI...
                        </p>
                    </div>
                </GlassCard>
            )}

            {/* Error State */}
            {error && !loading && (
                <GlassCard padding="md">
                    <div className="trend-error">
                        <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
                        <span style={{ color: 'var(--danger)' }}>{error}</span>
                    </div>
                </GlassCard>
            )}

            {/* Results */}
            {data && data.prediction && !loading && (
                <>
                    {/* Main Prediction Card */}
                    <GlassCard padding="lg" className="trend-card">
                        <div className="trend-card-header">
                            <div className="trend-card-title-row">
                                {getTrendIcon(data.prediction?.trend_direction || 'stable')}
                                <h3 className="trend-card-title">
                                    {METRIC_OPTIONS.find(m => m.value === data.metric_type)?.label || data.metric_type}
                                </h3>
                                <GlassBadge variant={getTrendVariant(data.prediction?.trend_direction || 'stable')} size="sm">
                                    {(data.prediction?.trend_direction || 'stable').toUpperCase()}
                                </GlassBadge>
                            </div>
                            <div className="trend-card-badges">
                                {getMethodBadge(method)}
                                <GlassBadge variant={getConfidenceVariant(data.prediction?.confidence || 0)} size="sm">
                                    {Math.round((data.prediction?.confidence || 0) * 100)}% confidence
                                </GlassBadge>
                            </div>
                        </div>

                        <div className="trend-predicted-value">
                            <span className="trend-predicted-number">
                                {typeof data.prediction.predicted_value === 'number'
                                    ? data.prediction.predicted_value.toLocaleString()
                                    : data.prediction.predicted_value}
                            </span>
                            <span className="trend-predicted-label">
                                predicted in next {data.horizon_days} days
                            </span>
                            {intervalSummary && (
                                <span className="trend-interval-note">{intervalSummary}</span>
                            )}
                        </div>

                        <div className="trend-reasoning">
                            <p>{data.prediction.reasoning}</p>
                        </div>

                        {data.prediction.historical_context && (
                            <div className="trend-historical">
                                <span className="trend-historical-label">Historical context:</span>
                                <p>{data.prediction.historical_context}</p>
                            </div>
                        )}
                    </GlassCard>

                    {/* Recommended Actions */}
                    {data.prediction.recommended_actions && data.prediction.recommended_actions.length > 0 && (
                        <GlassCard padding="md" className="trend-actions-card">
                            <h4 className="trend-actions-title">Recommended Actions</h4>
                            <ul className="trend-actions-list">
                                {data.prediction.recommended_actions.map((action, idx) => (
                                    <li key={idx} className="trend-action-item">
                                        <ChevronRight size={14} style={{ color: 'var(--primary-500)', flexShrink: 0, marginTop: 2 }} />
                                        <span>{action}</span>
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    )}
                </>
            )}
        </div>
    );
};

export default TrendChart;
